import { useState, useEffect, useCallback } from 'react';
import type { Node as DiagramNode, NodeMeta } from '../types';
import { calcNodeHeight } from '../utils/socketLayout';
import { DESC_H } from '../utils/socketLayout';

interface UseNodeInfoPanelOptions {
  infoPanelNodeId: string | null;
  nodes: DiagramNode[];
  updateNodeMeta: (id: string, updates: Partial<NodeMeta>) => void;
  updateNode: (id: string, updates: Partial<DiagramNode>) => void;
}

export function useNodeInfoPanel({
  infoPanelNodeId,
  nodes,
  updateNodeMeta,
  updateNode,
}: UseNodeInfoPanelOptions) {
  const infoNode = infoPanelNodeId ? nodes.find(n => n.id === infoPanelNodeId) ?? null : null;
  const meta = infoNode?.meta ?? {};

  // Controlled local state for description and tooltip — reset when panel opens for different node
  const [localDesc, setLocalDesc] = useState(meta.description ?? '');
  const [localTooltip, setLocalTooltip] = useState(meta.tooltip ?? '');
  useEffect(() => { setLocalDesc(infoNode?.meta?.description ?? ''); }, [infoPanelNodeId, infoNode?.meta?.description]);
  useEffect(() => { setLocalTooltip(infoNode?.meta?.tooltip ?? ''); }, [infoPanelNodeId, infoNode?.meta?.tooltip]);

  const handleMetaChange = useCallback((updates: Partial<NodeMeta>) => {
    if (!infoPanelNodeId) return;
    updateNodeMeta(infoPanelNodeId, updates);
    // When description is added/removed, update node height
    if ('description' in updates) {
      const node = nodes.find(n => n.id === infoPanelNodeId);
      if (node) {
        const nIn = node.sockets.filter(s => s.type === 'input').length;
        const nOut = node.sockets.filter(s => s.type === 'output').length;
        const epCount = (node.type === 'api' && Array.isArray(node.data?.endpoints)) ? node.data.endpoints.length : 0;
        const newH = calcNodeHeight(nIn, nOut, epCount) + (updates.description ? DESC_H : 0);
        updateNode(infoPanelNodeId, { size: { width: node.size.width, height: newH } });
      }
    }
  }, [infoPanelNodeId, updateNodeMeta, nodes, updateNode]);

  // ── Endpoints (api nodes) ─────────────────────────────────────────────────
  const infoEndpoints: string[] = (infoNode?.type === 'api' && Array.isArray(infoNode?.data?.endpoints))
    ? infoNode.data.endpoints as string[]
    : [];

  const handleAddEndpoint = useCallback(() => {
    if (!infoPanelNodeId || !infoNode) return;
    const newEndpoints = [...infoEndpoints, 'GET /'];
    const nIn = infoNode.sockets.filter(s => s.type === 'input').length;
    const nOut = infoNode.sockets.filter(s => s.type === 'output').length;
    const newH = calcNodeHeight(nIn, nOut, newEndpoints.length) + (infoNode.meta?.description ? DESC_H : 0);
    updateNode(infoPanelNodeId, {
      size: { width: infoNode.size.width, height: newH },
      data: { ...infoNode.data, endpoints: newEndpoints },
    });
  }, [infoPanelNodeId, infoNode, infoEndpoints, updateNode]);

  const handleRemoveEndpoint = useCallback((i: number) => {
    if (!infoPanelNodeId || !infoNode) return;
    const newEndpoints = infoEndpoints.filter((_, idx) => idx !== i);
    const nIn = infoNode.sockets.filter(s => s.type === 'input').length;
    const nOut = infoNode.sockets.filter(s => s.type === 'output').length;
    const newH = calcNodeHeight(nIn, nOut, newEndpoints.length) + (infoNode.meta?.description ? DESC_H : 0);
    updateNode(infoPanelNodeId, {
      size: { width: infoNode.size.width, height: newH },
      data: { ...infoNode.data, endpoints: newEndpoints },
    });
  }, [infoPanelNodeId, infoNode, infoEndpoints, updateNode]);

  const handleEndpointChange = useCallback((i: number, val: string) => {
    if (!infoPanelNodeId || !infoNode) return;
    const newEndpoints = infoEndpoints.map((ep, idx) => idx === i ? val : ep);
    updateNode(infoPanelNodeId, { data: { ...infoNode.data, endpoints: newEndpoints } });
  }, [infoPanelNodeId, infoNode, infoEndpoints, updateNode]);

  const handleAddLink = useCallback(
    () => handleMetaChange({ links: [...(meta.links ?? []), { label: '', url: '' }] }),
    [handleMetaChange, meta.links],
  );
  const handleRemoveLink = useCallback(
    (i: number) => handleMetaChange({ links: (meta.links ?? []).filter((_, idx) => idx !== i) }),
    [handleMetaChange, meta.links],
  );
  const handleLinkChange = useCallback(
    (i: number, field: 'label' | 'url', val: string) =>
      handleMetaChange({ links: (meta.links ?? []).map((l, idx) => idx === i ? { ...l, [field]: val } : l) }),
    [handleMetaChange, meta.links],
  );

  return {
    infoNode,
    meta,
    localDesc,
    setLocalDesc,
    localTooltip,
    setLocalTooltip,
    infoEndpoints,
    handleMetaChange,
    handleAddEndpoint,
    handleRemoveEndpoint,
    handleEndpointChange,
    handleAddLink,
    handleRemoveLink,
    handleLinkChange,
  };
}
